import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import z from 'zod';

import { DimensionUnit, WeightUnit } from '@invenco/common-interface/shared';
import { FulfillmentStrategy, SkuType } from '@invenco/products-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { NumberFormField } from '@/components/forms/number-form-field';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { skuTypeTitle } from '@/shared/title-maps';

const formSchema = z.object({
  type: z.enum(SkuType),
  name: z
    .string()
    .min(1, 'Enter a name')
    .refine((val) => val === val.trim(), {
      message: 'Please remove spaces from beginning and end of sku name',
    }),
  description: z.string().min(1, 'Enter a description'),
  barcode: z.string(),
  length: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  weight: z.number().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

export function CreateSkuDialogContent() {
  const { productsGateway } = useGateways();
  const navigate = useNavigate();

  const { mutate: saveSku } = useGatewayMutation({
    mutationFn: ({
      type,
      name,
      description,
      barcode,
      length,
      width,
      height,
      weight,
    }: FormValues) => {
      const hasSomeMeasurements = !!length || !!width || !!height || !!weight;

      return productsGateway.createSku({
        type,
        name,
        description,
        barcode,
        fulfillmentStrategy:
          type === SkuType.STANDARD ? FulfillmentStrategy.STOCK : FulfillmentStrategy.BUNDLE,
        measurement: hasSomeMeasurements
          ? {
              length: length ?? 0,
              width: width ?? 0,
              height: height ?? 0,
              dimensionUnit: DimensionUnit.CM,
              weight: weight ?? 0,
              weightUnit: WeightUnit.KG,
            }
          : undefined,
      });
    },
    onSuccess: (newSku) => {
      void navigate(`/products/skus/${newSku.id}`);
    },
    linkedQuery: ['skus', undefined], // undefined is replaced by id from response sku
    successMessage: 'SKU created',
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: SkuType.STANDARD,
      name: '',
      description: '',
      barcode: '',
      length: null,
      width: null,
      height: null,
      weight: null,
    },
  });

  const watchedType = form.watch('type');

  return (
    <FormDialogContent
      form={form}
      onSubmit={async (data) => {
        const result = await saveSku(data);
        return result;
      }}
    >
      <DialogHeader>
        <DialogTitle>Create SKU</DialogTitle>
        <DialogDescription>Enter the SKU name and description</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <SelectFormField control={form.control} name="type" label="SKU type" className="w-full">
          {[SkuType.STANDARD, SkuType.BUNDLE, SkuType.MYSTERY, SkuType.ALIAS].map((type) => (
            <SelectItem key={type} value={type}>
              {skuTypeTitle[type]}
            </SelectItem>
          ))}
        </SelectFormField>
        <TextFormField control={form.control} name="name" label="Name" />
        <TextFormField control={form.control} name="description" label="Description" />
        <TextFormField control={form.control} name="barcode" label="Barcode" />
        {watchedType === SkuType.STANDARD && (
          <div className="grid grid-cols-3 gap-2">
            <NumberFormField
              control={form.control}
              name="length"
              label={`Length (${DimensionUnit.CM})`}
            />
            <NumberFormField
              control={form.control}
              name="width"
              label={`Width (${DimensionUnit.CM})`}
            />
            <NumberFormField
              control={form.control}
              name="height"
              label={`Height (${DimensionUnit.CM})`}
            />
            <NumberFormField
              control={form.control}
              name="weight"
              label={`Weight (${WeightUnit.KG})`}
            />
          </div>
        )}
      </DialogBody>
    </FormDialogContent>
  );
}
